import React from 'react';
import { CommerceVariant, ProductOnUser } from 'common-ts/dist/models/Product';

// Page-level context for product detail pages. The page resolves WHICH
// product is in scope; the BlockProduct* blocks in the layout read it.
// Kept in its own file so both the page and the block library can import
// it without a cycle — same reasoning as CategoryPageContext.
//
// Blocks render null when this is absent, so dropping a product block into
// a Kategori or Halaman layout degrades quietly instead of crashing.
export interface ProductPageContextValue {
  productId: string;
  product: ProductOnUser;
  // Set inside the dashboard's preview iframe. Blocks use it to suppress
  // things a preview must not do — opening the fullscreen media overlay,
  // or offering the "write a review" dialog.
  isPreview?: boolean;
}

export const ProductPageContext =
  React.createContext<ProductPageContextValue | null>(null);

// Media state is SEPARATE from ProductPageContext on purpose. The page
// context value is stable for the page's lifetime; this one changes on
// every carousel tick. Merging them would re-render every block in the
// layout each time the gallery advances.
export interface ProductMediaContextValue {
  // Normalized display URLs (_familymedia stripped, _1080 applied).
  mediaUrls: string[];
  index: number;
  setIndex: (index: number) => void;
  overlayOpen: boolean;
  setOverlayOpen: (open: boolean) => void;
  // Called when the shopper interacts, to hold off the autoslide timer.
  freezeAutoslide: () => void;
}

export const ProductMediaContext =
  React.createContext<ProductMediaContextValue | null>(null);

// Variant selection is ALSO separate from ProductPageContext, for the same
// reason as media state: it changes on every chip click. The picker
// (PageProductDetailCommerceOptions) writes it; the price block and the
// page's gallery source read it. Like the other contexts, consumers fall
// back gracefully when it is absent (LinkPage sheet, preview iframe).
export interface VariantSelectionContextValue {
  selected: { [optionName: string]: string };
  setSelected: React.Dispatch<React.SetStateAction<{ [optionName: string]: string }>>;
  // Defined only when the product has options, all of them are selected,
  // and a variant matches. Option-less products stay undefined on purpose:
  // their price display keeps following the base product fields.
  displayVariant: CommerceVariant | undefined;
}

export const VariantSelectionContext =
  React.createContext<VariantSelectionContextValue | null>(null);
